import { useEffect, useState } from "react";
import { Scale, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Props { woId: string; pay: any[]; onSaved: (wo: any) => void }

export function PaySplitEditor({ woId, pay, onSaved }: Props) {
  const [shares, setShares] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const sig = pay.map((p) => `${p.installer_id}:${p.share}`).join(",");
  useEffect(() => { setShares(Object.fromEntries(pay.map((p) => [p.installer_id, String(Math.round(p.share * 10000) / 100)]))); }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = Object.values(shares).reduce((s, v) => s + (Number(v) || 0), 0);
  const ok = Math.abs(total - 100) < 0.01 && Object.values(shares).every((v) => Number(v) >= 0);
  const submit = async (body: Record<string, number>, msg: string) => {
    setBusy(true);
    try { const r = await api.put(`/work-orders/${woId}/pay-split`, { shares: body }); onSaved(r.data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-md border border-dashed border-gray-300 p-2.5" data-testid="wo-pay-split">
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-500"><span className="flex items-center gap-1.5"><Scale size={12} />Pay split between installers</span>
        <span data-testid="wo-pay-split-total" className={ok ? "text-green-700" : "text-red-600"}>{Math.round(total * 100) / 100}%</span></div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {pay.map((p) => (
          <label key={p.installer_id} className="flex items-center gap-2 text-xs">
            <span className="min-w-0 flex-1 truncate text-gray-800">{p.installer_name}</span>
            <Input data-testid={`wo-pay-split-${p.installer_id}`} type="number" min="0" max="100" step="0.5" className="h-8 w-24 text-right text-xs" value={shares[p.installer_id] ?? ""} onChange={(e) => setShares({ ...shares, [p.installer_id]: e.target.value })} />
            <span className="text-gray-400">%</span>
          </label>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-end gap-2">
        {!ok && <span className="mr-auto text-[11px] text-red-600" data-testid="wo-pay-split-error">Shares must add up to 100%</span>}
        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="wo-pay-split-even-btn" disabled={busy} onClick={() => submit({}, "Pay split reset to even")}><RotateCcw size={12} className="mr-1" />Even split</Button>
        <Button size="sm" className="h-7 bg-blue-700 text-xs hover:bg-blue-800" data-testid="wo-pay-split-save-btn" disabled={busy || !ok} onClick={() => submit(Object.fromEntries(Object.entries(shares).map(([k, v]) => [k, Number(v) || 0])), "Pay split saved")}>{busy ? "Saving…" : "Save split"}</Button>
      </div>
    </div>
  );
}
